import React, { useEffect, useMemo, useState } from 'react';
import { authFetch } from '../utils/apiHelper';
import { API_URL } from '../config';
import { useAuth } from '../auth/AuthContext';
import { ADMIN_WORKSPACE, refreshWorkspaces, currentWorkspaceId, switchWorkspace } from '../utils/workspace';
import { modalPrimaryBtn, modalSecondaryBtn, modalDangerBtn, MODAL_BTN_ROW, modalCard, modalTitle, modalBody } from '../modalButtons';
import ModalOverlay from './ModalOverlay';

// Users & workspaces (sysAdmin only): opened by the 'Manage' item in the
// top-nav settings dropdown on every page (shown only while a sysAdmin is
// logged in — body.role-sysadmin, AuthContext.js).
//  - Workspaces: create, rename, duplicate (same splats + members), delete
//    (with everything in it); per workspace, which splats it contains and
//    which users are members. The built-in Admin workspace (every splat,
//    every sysAdmin) can't be edited.
//  - Users: each user's workspaces (a normal user always keeps at least
//    one), invite (name + email + workspaces), delete. sysAdmins are greyed
//    out — they use every workspace.
// The backend enforces sysAdmin on every one of these calls (backend/
// index.js, "Users & workspaces (sysAdmin only)").

const post = async (payload) => {
  const res = await authFetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
};

const font = 'Inter, Arial, sans-serif';
const rowStyle = (disabled) => ({
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', boxSizing: 'border-box',
  padding: '10px 4px', border: 'none', borderBottom: '1px solid #f0f0f0', background: 'none',
  cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1, fontFamily: font,
});
const listStyle = { maxHeight: 300, overflowY: 'auto', margin: '0 0 4px', borderTop: '1px solid #eee' };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', height: 36, padding: '0 10px', border: '1px solid #ddd',
  borderRadius: 7, fontSize: 13, fontFamily: font, marginBottom: 8,
};
const smallText = { fontSize: 11, color: '#999', fontFamily: font };
const errorText = { color: '#ef4444', fontSize: 12, margin: '8px 0 0', fontFamily: font };
const noticeText = { ...smallText, color: '#16a34a', margin: '8px 0 0' };
const linkBtn = { background: 'none', border: 'none', padding: 0, fontSize: 12, fontFamily: font, color: '#1a1a1a', textDecoration: 'underline', cursor: 'pointer' };
const dangerLink = { ...linkBtn, color: '#ef4444' };
const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const checkboxStyle = { accentColor: '#1a1a1a', width: 16, height: 16, flexShrink: 0, cursor: 'pointer' };
const tabStyle = (active) => ({
  flex: 1, padding: '8px 0 10px', background: 'none', border: 'none', cursor: 'pointer',
  borderBottom: active ? '2px solid #1a1a1a' : '2px solid transparent', marginBottom: -1,
  color: active ? '#1a1a1a' : '#888', fontWeight: active ? 600 : 400, fontSize: 13, fontFamily: font,
});

const displayName = (u) => [u.givenName, u.familyName].filter(Boolean).join(' ');
const sameSet = (a, b) => a.size === b.size && [...a].every((x) => b.has(x));

const Tabs = ({ tabs, active, onChange }) => (
  <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: '1px solid #eee' }}>
    {tabs.map((t) => (
      <button key={t.key} type="button" onClick={() => onChange(t.key)} style={tabStyle(active === t.key)}>{t.label}</button>
    ))}
  </div>
);

// A filterable checkbox list. items: [{ id, label, sub?, disabled?, title? }]
const CheckList = ({ items, checked, onToggle, onSetMany, placeholder, empty }) => {
  const [filter, setFilter] = useState('');
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return items.filter((i) => !q || `${i.label} ${i.sub || ''}`.toLowerCase().includes(q));
  }, [items, filter]);
  const selectable = shown.filter((i) => !i.disabled);
  const allOn = selectable.length > 0 && selectable.every((i) => checked.has(i.id));
  return (
    <>
      <input style={inputStyle} placeholder={placeholder} value={filter} onChange={(e) => setFilter(e.target.value)} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={smallText}>{items.filter((i) => checked.has(i.id)).length} of {items.filter((i) => !i.disabled).length} selected</span>
        <button type="button" style={linkBtn} disabled={!selectable.length}
          onClick={() => onSetMany(selectable.map((i) => i.id), !allOn)}>
          {allOn ? 'Clear shown' : 'Select shown'}
        </button>
      </div>
      <div style={listStyle}>
        {shown.length === 0
          ? <p style={{ ...smallText, padding: '12px 4px' }}>{empty}</p>
          : shown.map((i) => (
            <label key={i.id} style={rowStyle(i.disabled)} title={i.title}>
              <input type="checkbox" checked={i.disabled ? false : checked.has(i.id)} disabled={i.disabled}
                onChange={() => onToggle(i.id)} style={checkboxStyle} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, color: '#1a1a1a', ...ellipsis }}>{i.label}</span>
                {i.sub && <span style={{ ...smallText, display: 'block', ...ellipsis }}>{i.sub}</span>}
              </span>
            </label>
          ))}
      </div>
    </>
  );
};

const toggleIn = (set, id) => { const next = new Set(set); if (next.has(id)) next.delete(id); else next.add(id); return next; };
const setMany = (set, ids, on) => { const next = new Set(set); ids.forEach((id) => (on ? next.add(id) : next.delete(id))); return next; };

const UsersModal = () => {
  const { hasRole, user: me } = useAuth();
  const isSysAdmin = hasRole('sysAdmin');
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('workspaces'); // 'workspaces' | 'users'
  // view: list | workspace | newWorkspace | duplicate | rename | confirmDeleteWorkspace | user | invite | confirmDeleteUser
  const [view, setView] = useState('list');
  const [users, setUsers] = useState(null);
  const [workspaces, setWorkspaces] = useState(null);
  const [models, setModels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Workspace being edited.
  const [ws, setWs] = useState(null);
  const [wsTab, setWsTab] = useState('splats'); // 'splats' | 'members'
  const [wsModels, setWsModels] = useState(new Set());
  const [wsMembers, setWsMembers] = useState(new Set());
  const [newName, setNewName] = useState('');
  // User being edited.
  const [selUser, setSelUser] = useState(null);
  const [userWs, setUserWs] = useState(new Set());
  const [invite, setInvite] = useState({ email: '', givenName: '', familyName: '', workspaceIds: new Set() });

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(''), 2500); };
  const fail = (e) => setError(e.message || String(e));

  // initial: the first load when the modal opens; every later reload follows
  // a change, so the settings menu's workspace dropdown is refreshed too.
  const reload = async (initial = false) => {
    if (!initial) refreshWorkspaces();
    const [u, w] = await Promise.all([post({ listUsers: true }), post({ listWorkspaces: true })]);
    setUsers(u.users || []);
    setWorkspaces(w.workspaces || []);
    return { users: u.users || [], workspaces: w.workspaces || [] };
  };

  useEffect(() => {
    const handle = (e) => {
      if (e.detail !== 'users' || !isSysAdmin) return;
      setOpen(true);
      setView('list');
      setError('');
      setNotice('');
      setUsers(null);
      setWorkspaces(null);
      reload(true).catch(fail);
      setModels(null);
      // Every splat (the Admin workspace lists them all).
      authFetch(`${API_URL}?listModels=true&ws=${ADMIN_WORKSPACE}`)
        .then((res) => (res.ok ? res.json() : Promise.reject()))
        .then((d) => setModels(d.models || []))
        .catch(() => setModels([]));
    };
    window.addEventListener('dashboardNav', handle);
    return () => window.removeEventListener('dashboardNav', handle);
  }, [isSysAdmin]);

  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [open]);

  const go = (v) => { setView(v); setError(''); };
  const realWorkspaces = (workspaces || []).filter((w) => !w.system);
  const wsNameOf = (id) => (workspaces || []).find((w) => w.workspaceId === id)?.name || id;
  const modelName = (id) => (models || []).find((m) => m.modelId === id)?.name || id;

  // ─── actions ───────────────────────────────────────────────────────────
  const openWorkspace = (w) => {
    setWs(w);
    setWsModels(new Set(w.modelIds));
    setWsMembers(new Set(w.memberIds));
    setWsTab('splats');
    go('workspace');
  };

  const renameWs = async () => {
    setBusy(true);
    setError('');
    try {
      await post({ renameWorkspace: { workspaceId: ws.workspaceId, name: newName } });
      const { workspaces: fresh } = await reload();
      const updated = fresh.find((w) => w.workspaceId === ws.workspaceId);
      if (updated) setWs(updated);
      go('workspace');
      flash('Renamed');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const saveWorkspace = async () => {
    setBusy(true);
    setError('');
    try {
      if (!sameSet(wsModels, new Set(ws.modelIds))) await post({ setWorkspaceModels: { workspaceId: ws.workspaceId, modelIds: [...wsModels] } });
      if (!sameSet(wsMembers, new Set(ws.memberIds))) await post({ setWorkspaceMembers: { workspaceId: ws.workspaceId, userIds: [...wsMembers] } });
      const { workspaces: fresh } = await reload();
      const updated = fresh.find((w) => w.workspaceId === ws.workspaceId);
      if (updated) openWorkspace(updated);
      flash('Saved');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const createWs = async () => {
    setBusy(true);
    setError('');
    try {
      const d = await post({ createWorkspace: { name: newName } });
      const { workspaces: fresh } = await reload();
      setNewName('');
      const created = fresh.find((w) => w.workspaceId === d.workspaceId);
      if (created) openWorkspace(created); else go('list');
      flash('Workspace created — pick its splats and members');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const duplicateWs = async () => {
    setBusy(true);
    setError('');
    try {
      const d = await post({ duplicateWorkspace: { workspaceId: ws.workspaceId, name: newName } });
      const { workspaces: fresh } = await reload();
      setNewName('');
      const created = fresh.find((w) => w.workspaceId === d.workspaceId);
      if (created) openWorkspace(created); else go('list');
      flash('Workspace duplicated');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const deleteWs = async () => {
    setBusy(true);
    setError('');
    try {
      const deletedId = ws.workspaceId;
      const wasCurrent = currentWorkspaceId() === deletedId;
      await post({ deleteWorkspace: { workspaceId: deletedId } });
      // Deleted the one this page is in: go to the Dashboard of another.
      if (wasCurrent) { switchWorkspace(ADMIN_WORKSPACE); return; }
      await reload();
      setWs(null);
      go('list');
      flash('Workspace deleted');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const openUser = (u) => {
    if (u.sysAdmin) return;
    setSelUser(u);
    setUserWs(new Set(u.workspaceIds));
    go('user');
  };

  const saveUser = async () => {
    setBusy(true);
    setError('');
    try {
      await post({ setUserWorkspaces: { userId: selUser.userId, workspaceIds: [...userWs] } });
      const { users: fresh } = await reload();
      const updated = fresh.find((u) => u.userId === selUser.userId);
      if (updated) { setSelUser(updated); setUserWs(new Set(updated.workspaceIds)); }
      flash('Saved');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const deleteUserNow = async () => {
    setBusy(true);
    setError('');
    try {
      await post({ deleteUser: { userId: selUser.userId } });
      await reload();
      setSelUser(null);
      go('list');
      flash('User deleted');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const sendInvite = async () => {
    setBusy(true);
    setError('');
    try {
      await post({ inviteUser: { ...invite, workspaceIds: [...invite.workspaceIds] } });
      const sentTo = invite.email.trim();
      setInvite({ email: '', givenName: '', familyName: '', workspaceIds: new Set() });
      await reload();
      go('list');
      flash(`Invite sent to ${sentTo}`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !isSysAdmin) return null;
  const card = { ...modalCard, maxWidth: 480 };
  const primary = (disabled) => ({ ...modalPrimaryBtn, flex: 1, opacity: disabled ? 0.5 : 1 });
  const Messages = () => (
    <>
      {error && <p style={errorText}>{error}</p>}
      {notice && <p style={noticeText}>{notice}</p>}
    </>
  );
  const shell = (children) => (
    <ModalOverlay onClick={(e) => e.stopPropagation()}>
      <div className="modal-content" style={card} onClick={(e) => e.stopPropagation()}>{children}</div>
    </ModalOverlay>
  );
  const workspaceItems = realWorkspaces.map((w) => ({ id: w.workspaceId, label: w.name, sub: `${w.modelIds.length} splat${w.modelIds.length === 1 ? '' : 's'}` }));

  // ─── workspace views ─────────────────────────────────────────────────
  if (view === 'rename' && ws) {
    const unchanged = !newName.trim() || newName.trim() === ws.name;
    return shell(
      <>
        <p style={modalTitle}>Rename “{ws.name}”</p>
        <p style={modalBody}>Members see the new name in their settings menu.</p>
        <input style={inputStyle} placeholder="Workspace name" value={newName} autoFocus maxLength={60}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !unchanged && !busy) renameWs(); }} />
        <Messages />
        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={() => go('workspace')} style={{ ...modalSecondaryBtn, flex: 1 }}>Back</button>
          <button type="button" onClick={renameWs} disabled={busy || unchanged} style={primary(busy || unchanged)}>
            {busy ? 'Saving…' : 'Rename'}
          </button>
        </div>
      </>,
    );
  }

  if (view === 'newWorkspace' || view === 'duplicate') {
    const dup = view === 'duplicate';
    return shell(
      <>
        <p style={modalTitle}>{dup ? `Duplicate “${ws.name}”` : 'New workspace'}</p>
        <p style={modalBody}>{dup ? 'Same splats and members, with its own empty captures, labels and share links.' : 'Give it a name, then pick its splats and members.'}</p>
        <input style={inputStyle} placeholder="Workspace name" value={newName} autoFocus maxLength={60}
          onChange={(e) => setNewName(e.target.value)} />
        <Messages />
        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={() => go(dup ? 'workspace' : 'list')} style={{ ...modalSecondaryBtn, flex: 1 }}>Back</button>
          <button type="button" onClick={dup ? duplicateWs : createWs} disabled={busy || !newName.trim()} style={primary(busy || !newName.trim())}>
            {busy ? 'Saving…' : dup ? 'Duplicate' : 'Create'}
          </button>
        </div>
      </>,
    );
  }

  if (view === 'confirmDeleteWorkspace' && ws) {
    return shell(
      <>
        <p style={modalTitle}>Delete “{ws.name}”?</p>
        <p style={modalBody}>
          Everything in this workspace is permanently deleted: all its captures (images and their settings), labels, favorites and share links.
          {' '}The splats themselves stay. This can’t be undone.
        </p>
        <Messages />
        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={() => go('workspace')} style={{ ...modalSecondaryBtn, flex: 1 }}>Cancel</button>
          <button type="button" onClick={deleteWs} disabled={busy} style={{ ...modalDangerBtn, flex: 1, opacity: busy ? 0.5 : 1 }}>
            {busy ? 'Deleting…' : 'Delete workspace'}
          </button>
        </div>
      </>,
    );
  }

  if (view === 'workspace' && ws) {
    const dirty = !sameSet(wsModels, new Set(ws.modelIds)) || !sameSet(wsMembers, new Set(ws.memberIds));
    const memberItems = (users || []).map((u) => ({
      id: u.userId,
      label: displayName(u) || u.email,
      sub: u.sysAdmin ? `${u.email} · sysAdmin, uses every workspace` : u.email,
      disabled: u.sysAdmin,
    }));
    return shell(
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12 }}>
          <p style={{ ...modalTitle, margin: 0, ...ellipsis }}>{ws.name}</p>
          <span style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
            <button type="button" style={linkBtn} onClick={() => { setNewName(ws.name); go('rename'); }}>Rename</button>
            <button type="button" style={linkBtn} onClick={() => { setNewName(`${ws.name} copy`); go('duplicate'); }}>Duplicate</button>
            <button type="button" style={dangerLink} onClick={() => go('confirmDeleteWorkspace')}>Delete</button>
          </span>
        </div>
        <Tabs tabs={[{ key: 'splats', label: `Splats (${wsModels.size})` }, { key: 'members', label: `Members (${wsMembers.size})` }]} active={wsTab} onChange={setWsTab} />
        {wsTab === 'splats'
          ? (models === null
            ? <p style={smallText}>Loading…</p>
            : <CheckList items={models.map((m) => ({ id: m.modelId, label: m.name || m.modelId }))} checked={wsModels}
                onToggle={(id) => setWsModels((s) => toggleIn(s, id))} onSetMany={(ids, on) => setWsModels((s) => setMany(s, ids, on))}
                placeholder="Filter splats" empty="No splats." />)
          : <CheckList items={memberItems} checked={wsMembers}
              onToggle={(id) => setWsMembers((s) => toggleIn(s, id))} onSetMany={(ids, on) => setWsMembers((s) => setMany(s, ids, on))}
              placeholder="Filter users" empty="No users." />}
        <Messages />
        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={() => go('list')} style={{ ...modalSecondaryBtn, flex: 1 }}>Back</button>
          <button type="button" onClick={saveWorkspace} disabled={busy || !dirty} style={primary(busy || !dirty)}>
            {busy ? 'Saving…' : 'Apply'}
          </button>
        </div>
      </>,
    );
  }

  // ─── user views ──────────────────────────────────────────────────────
  if (view === 'invite') {
    const complete = !!(invite.email.trim() && invite.givenName.trim() && invite.familyName.trim() && invite.workspaceIds.size);
    return shell(
      <>
        <p style={modalTitle}>Invite user</p>
        <p style={modalBody}>They get an email with a temporary password and work in the workspaces you pick.</p>
        <input style={inputStyle} type="email" placeholder="Email" value={invite.email} autoFocus
          onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={inputStyle} placeholder="First name" value={invite.givenName}
            onChange={(e) => setInvite({ ...invite, givenName: e.target.value })} />
          <input style={inputStyle} placeholder="Last name" value={invite.familyName}
            onChange={(e) => setInvite({ ...invite, familyName: e.target.value })} />
        </div>
        <CheckList items={workspaceItems} checked={invite.workspaceIds}
          onToggle={(id) => setInvite((v) => ({ ...v, workspaceIds: toggleIn(v.workspaceIds, id) }))}
          onSetMany={(ids, on) => setInvite((v) => ({ ...v, workspaceIds: setMany(v.workspaceIds, ids, on) }))}
          placeholder="Filter workspaces" empty="No workspaces yet — create one first." />
        <Messages />
        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={() => go('list')} style={{ ...modalSecondaryBtn, flex: 1 }}>Back</button>
          <button type="button" onClick={sendInvite} disabled={busy || !complete} style={primary(busy || !complete)}>
            {busy ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </>,
    );
  }

  if (view === 'confirmDeleteUser' && selUser) {
    return shell(
      <>
        <p style={modalTitle}>Delete {displayName(selUser) || selUser.email}?</p>
        <p style={modalBody}>Their login is removed and they lose access to every workspace. Captures and labels they made stay in the workspaces. This can’t be undone.</p>
        <Messages />
        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={() => go('user')} style={{ ...modalSecondaryBtn, flex: 1 }}>Cancel</button>
          <button type="button" onClick={deleteUserNow} disabled={busy} style={{ ...modalDangerBtn, flex: 1, opacity: busy ? 0.5 : 1 }}>
            {busy ? 'Deleting…' : 'Delete user'}
          </button>
        </div>
      </>,
    );
  }

  if (view === 'user' && selUser) {
    const dirty = !sameSet(userWs, new Set(selUser.workspaceIds));
    return shell(
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 12 }}>
          <p style={{ ...modalTitle, margin: 0, ...ellipsis }}>{displayName(selUser) || selUser.email}</p>
          {selUser.userId !== me?.userId && <button type="button" style={dangerLink} onClick={() => go('confirmDeleteUser')}>Delete user</button>}
        </div>
        <p style={modalBody}>{selUser.email} · workspaces this user works in (at least one).</p>
        <CheckList items={workspaceItems} checked={userWs}
          onToggle={(id) => setUserWs((s) => toggleIn(s, id))} onSetMany={(ids, on) => setUserWs((s) => setMany(s, ids, on))}
          placeholder="Filter workspaces" empty="No workspaces." />
        <Messages />
        <div style={MODAL_BTN_ROW}>
          <button type="button" onClick={() => go('list')} style={{ ...modalSecondaryBtn, flex: 1 }}>Back</button>
          <button type="button" onClick={saveUser} disabled={busy || !dirty || !userWs.size} style={primary(busy || !dirty || !userWs.size)}>
            {busy ? 'Saving…' : 'Apply'}
          </button>
        </div>
      </>,
    );
  }

  // ─── lists ───────────────────────────────────────────────────────────
  return shell(
    <>
      <p style={{ ...modalTitle, marginBottom: 12 }}>Users &amp; workspaces</p>
      <Tabs tabs={[{ key: 'workspaces', label: 'Workspaces' }, { key: 'users', label: 'Users' }]} active={tab} onChange={(t) => { setTab(t); setError(''); }} />
      {tab === 'workspaces' ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={smallText}>Splats, members, and their own captures and labels.</span>
            <button type="button" style={linkBtn} onClick={() => { setNewName(''); go('newWorkspace'); }}>New workspace</button>
          </div>
          <div style={listStyle}>
            {workspaces === null
              ? <p style={{ ...smallText, padding: '12px 4px' }}>Loading…</p>
              : workspaces.map((w) => (
                <button key={w.workspaceId} type="button" style={rowStyle(w.system)} disabled={w.system} onClick={() => openWorkspace(w)}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, color: '#1a1a1a', ...ellipsis }}>{w.name}</span>
                    <span style={{ ...smallText, display: 'block', ...ellipsis }}>
                      {w.system ? 'Built in · every splat · sysAdmins only'
                        : `${w.modelIds.length} splat${w.modelIds.length === 1 ? '' : 's'} · ${w.memberIds.length} member${w.memberIds.length === 1 ? '' : 's'}`}
                    </span>
                  </span>
                  {!w.system && <span style={{ color: '#bbb', fontSize: 16 }}>›</span>}
                </button>
              ))}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={smallText}>Pick a user to choose their workspaces.</span>
            <button type="button" style={linkBtn} onClick={() => go('invite')}>Invite user</button>
          </div>
          <div style={listStyle}>
            {users === null
              ? <p style={{ ...smallText, padding: '12px 4px' }}>Loading…</p>
              : users.map((u) => (
                <button key={u.userId} type="button" style={rowStyle(u.sysAdmin)} onClick={() => openUser(u)} disabled={u.sysAdmin}
                  title={u.sysAdmin ? 'sysAdmin — uses every workspace' : undefined}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, color: '#1a1a1a', ...ellipsis }}>{displayName(u) || u.email}</span>
                    <span style={{ ...smallText, display: 'block', ...ellipsis }}>
                      {displayName(u) ? `${u.email} · ` : ''}
                      {u.sysAdmin ? 'sysAdmin · every workspace' : (u.workspaceIds.map(wsNameOf).join(', ') || 'no workspace')}
                      {u.status === 'FORCE_CHANGE_PASSWORD' ? ' · invited' : ''}
                      {u.enabled === false ? ' · disabled' : ''}
                    </span>
                  </span>
                  {!u.sysAdmin && <span style={{ color: '#bbb', fontSize: 16 }}>›</span>}
                </button>
              ))}
          </div>
        </>
      )}
      <Messages />
      <div style={MODAL_BTN_ROW}>
        <button type="button" onClick={() => setOpen(false)} style={{ ...modalSecondaryBtn, flex: 1 }}>Close</button>
      </div>
    </>,
  );
};

export default UsersModal;
